import React from 'react';
import stormiumTechLogo from './stormium-tech-logo.png';
import { legalContent } from './generated/legalContent';

type LegalDocument = {
  slug: string;
  title: string;
};

type MarkdownBlock =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'paragraph'; lines: string[] }
  | { kind: 'list'; ordered: boolean; items: string[] };

const inlineTokenPattern = /(\*\*[^*]+\*\*)/g;

function renderInline(text: string, keyPrefix: string) {
  return text.split(inlineTokenPattern).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`${keyPrefix}-strong-${index}`}>{part.slice(2, -2)}</strong>;
    }

    return <React.Fragment key={`${keyPrefix}-text-${index}`}>{part}</React.Fragment>;
  });
}

function parseMarkdown(markdown: string): MarkdownBlock[] {
  const blocks: MarkdownBlock[] = [];
  let paragraphLines: string[] = [];
  let list: Extract<MarkdownBlock, { kind: 'list' }> | null = null;

  const flushParagraph = () => {
    if (paragraphLines.length > 0) {
      blocks.push({ kind: 'paragraph', lines: paragraphLines });
      paragraphLines = [];
    }
  };

  const flushList = () => {
    if (list) {
      blocks.push(list);
      list = null;
    }
  };

  markdown.replace(/\r\n/g, '\n').split('\n').forEach((line) => {
    const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);
    const listMatch = line.match(/^\s*([-*]|\d+\.)\s+(.+)$/);

    if (headingMatch) {
      flushParagraph();
      flushList();
      blocks.push({
        kind: 'heading',
        level: headingMatch[1].length,
        text: headingMatch[2]
      });
      return;
    }

    if (listMatch) {
      flushParagraph();
      const ordered = /\d+\./.test(listMatch[1]);

      if (!list || list.ordered !== ordered) {
        flushList();
        list = { kind: 'list', ordered, items: [] };
      }

      list.items.push(listMatch[2]);
      return;
    }

    if (line.trim() === '') {
      flushParagraph();
      flushList();
      return;
    }

    flushList();
    paragraphLines.push(line);
  });

  flushParagraph();
  flushList();
  return blocks;
}

function MarkdownDocument({ markdown }: { markdown: string }) {
  return (
    <>
      {parseMarkdown(markdown).map((block, index) => {
        if (block.kind === 'heading') {
          const Heading = `h${Math.min(block.level, 3)}` as keyof React.JSX.IntrinsicElements;
          return <Heading key={`heading-${index}`}>{renderInline(block.text, `heading-${index}`)}</Heading>;
        }

        if (block.kind === 'list') {
          const List = block.ordered ? 'ol' : 'ul';
          return (
            <List key={`list-${index}`}>
              {block.items.map((item, itemIndex) => (
                <li key={`list-${index}-item-${itemIndex}`}>
                  {renderInline(item, `list-${index}-item-${itemIndex}`)}
                </li>
              ))}
            </List>
          );
        }

        return (
          <p key={`paragraph-${index}`}>
            {block.lines.map((line, lineIndex) => (
              <React.Fragment key={`paragraph-${index}-line-${lineIndex}`}>
                {renderInline(line.replace(/\s{2}$/, ''), `paragraph-${index}-line-${lineIndex}`)}
                {lineIndex < block.lines.length - 1 && (line.endsWith('  ') ? <br /> : ' ')}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </>
  );
}

function LegalFooter() {
  return (
    <footer className="site-footer">
      <hr className="footer-divider" aria-hidden="true" />
      <div className="page-width footer-layout">
        <p>© 2026 Stormium Tech LLC. All rights reserved.</p>
        <nav aria-label="Footer navigation">
          <a href="/">Home</a>
          <a href="/privacy">Privacy</a>
          <a href="/terms-of-service">Terms</a>
        </nav>
      </div>
    </footer>
  );
}

export default function LegalPage({ document }: { document: LegalDocument }) {
  const markdown = legalContent[document.slug];

  return (
    <div className="legal-page" id="top">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>

      <div className="legal-header-shell">
        <header className="site-header legal-header">
          <a className="brand" href="/" aria-label="Stormium Tech home">
            <img className="brand-logo" src={stormiumTechLogo} alt="Stormium Tech logo" />
            <span>Stormium Tech</span>
          </a>

          <nav className="primary-nav" aria-label="Primary navigation">
            <a href="/#about">About</a>
            <a href="/#contact">Contact</a>
          </nav>
        </header>
      </div>

      <main id="main-content" className="legal-main">
        <article className="page-width legal-document" aria-busy={!markdown}>
          <p className="eyebrow eyebrow-dark">Stormium Tech legal</p>
          {markdown && <MarkdownDocument markdown={markdown} />}
          {!markdown && <p>We could not load this document. Please contact hello@stormiumtech.com.</p>}
        </article>
      </main>

      <LegalFooter />
    </div>
  );
}
